import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  signInWithPopup,
  GoogleAuthProvider
} from 'firebase/auth';
import { auth } from '../firebase';
import { userService, UserProfile, Notification } from '../services/userService';
import { clearActingOrganizationId } from '../utils/actingOrganization';
import { sendAccountVerificationEmail } from '../utils/emailVerification';

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  notifications: Notification[];
  unreadNotifications: number;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (options?: { userType?: 'store' | 'vendor_supplier' }) => Promise<void>;
  register: (email: string, password: string, displayName: string, userType?: 'store' | 'vendor_supplier') => Promise<void>;
  logout: () => Promise<void>;
  updateUserProfile: (updates: Partial<UserProfile>) => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  markNotificationAsRead: (notificationId: string) => Promise<void>;
  markAllNotificationsAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  refreshNotifications: () => Promise<void>;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  const unreadNotifications = notifications.filter(n => !n.read).length;
  const isSuperAdmin = userProfile?.role === 'super_admin';
  const isAdmin = isSuperAdmin || userProfile?.role === 'admin';

  const loadUserProfile = async (user: User) => {
    try {
      let profile = await userService.getUserProfile(user.uid);
      if (!profile) {
        const signedUpWithGoogle = user.providerData.some(
          (provider) => provider.providerId === 'google.com'
        );
        // Created by the server without userType; the onboarding flow sets it
        // (unless loginWithGoogle already created the doc with a selected type).
        profile = await userService.saveOwnProfile(user, {
          ...(user.displayName ? { displayName: user.displayName } : {}),
          signupSource: signedUpWithGoogle ? 'google' : 'email',
        });
      } else {
        // Motzu staff are promoted by the server once their address is
        // verified; the browser only asks it to look.
        const isMotzuStaff = (profile.email || '').toLowerCase().endsWith('@motzulabs.com');
        const isAdmin = profile.role === 'admin' || profile.role === 'super_admin';
        if (isMotzuStaff && !isAdmin && user.emailVerified) {
          profile = await userService.saveOwnProfile(user);
        }
      }

      setUserProfile(profile);
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const loadNotifications = async (userId: string) => {
    try {
      const userNotifications = await userService.getNotifications(userId);
      setNotifications(userNotifications);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const loginWithGoogle = async (options?: { userType?: 'store' | 'vendor_supplier' }) => {
    const provider = new GoogleAuthProvider();
    const { user } = await signInWithPopup(auth, provider);

    // Ensure the profile exists before callers navigate into onboarding.
    // Signup can pass a selected account type; plain login leaves type unset.
    let profile = await userService.getUserProfile(user.uid);
    if (!profile) {
      profile = await userService.saveOwnProfile(user, {
        ...(user.displayName ? { displayName: user.displayName } : {}),
        ...(options?.userType ? { userType: options.userType } : {}),
        signupSource: 'google',
      });
    } else if (options?.userType && !profile.userType) {
      profile = await userService.saveOwnProfile(user, { userType: options.userType });
    }

    setCurrentUser(user);
    setUserProfile(profile);
  };

  const register = async (email: string, password: string, displayName: string, userType: 'store' | 'vendor_supplier' = 'store') => {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(user, { displayName });
    
    // The profile is created by the server, with the chosen account type.
    await userService.saveOwnProfile(user, {
      displayName: user.displayName || displayName,
      userType,
      signupSource: 'email',
    });

    try {
      await sendAccountVerificationEmail(user);
    } catch (error) {
      // The account exists. The verify page sends the link again and shows the error.
      console.error('Verification email was not sent:', error);
    }
  };

  const logout = async () => {
    const uid = currentUser?.uid;
    await signOut(auth);
    clearActingOrganizationId(uid);
    setUserProfile(null);
    setNotifications([]);
  };

  // Saves what a user may change on their own profile (the display name) and
  // re-syncs the verified flag from the ID token; anything else in `updates`
  // is server-owned and ignored.
  const updateUserProfile = async (updates: Partial<UserProfile>) => {
    if (!currentUser) return;
    const profile = await userService.saveOwnProfile(
      currentUser,
      updates.displayName ? { displayName: updates.displayName } : {}
    );
    setUserProfile(profile);
  };

  const refreshUserProfile = async () => {
    if (!currentUser) return;
    await loadUserProfile(currentUser);
  };

  const markNotificationAsRead = async (notificationId: string) => {
    await userService.markNotificationAsRead(notificationId);
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
  };

  const markAllNotificationsAsRead = async () => {
    if (!currentUser) return;
    await userService.markAllNotificationsAsRead(currentUser.uid);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const deleteNotification = async (notificationId: string) => {
    await userService.deleteNotification(notificationId);
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const refreshNotifications = async () => {
    if (!currentUser) return;
    await loadNotifications(currentUser.uid);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await loadUserProfile(user);
      } else {
        setUserProfile(null);
        setNotifications([]);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Real-time notifications: keep the list in sync while the user is logged in
  const currentUserId = currentUser?.uid;
  useEffect(() => {
    if (!currentUserId) return;
    const unsubscribe = userService.subscribeToNotifications(currentUserId, setNotifications);
    return unsubscribe;
  }, [currentUserId]);

  const value = {
    currentUser,
    userProfile,
    notifications,
    unreadNotifications,
    login,
    loginWithGoogle,
    register,
    logout,
    updateUserProfile,
    refreshUserProfile,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    deleteNotification,
    refreshNotifications,
    isAdmin,
    isSuperAdmin,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
